import { useCallback, useMemo } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useDraggable } from '@dnd-kit/core'
import { useLingui } from '@lingui/react/macro'
import { ClockIcon } from 'lucide-react'

import { Duration } from '~/components/atoms/duration'
import { TIME_SLOT_HEIGHT, type TimeslotBounds } from './helpers'
import type { BookingOption } from '../step-time'

const { getTimeAsMinutes } = dateTimeUtils.utils

export const ResizeHandle = ({
	position,
	draggable,
}: {
	position: 'top' | 'bottom'
	draggable: Pick<
		ReturnType<typeof useDraggable>,
		'setNodeRef' | 'transform' | 'attributes' | 'listeners'
	>
}) => (
	<div
		ref={draggable.setNodeRef}
		{...draggable.attributes}
		{...draggable.listeners}
		className='h-2 cursor-ns-resize sm:h-[5px]'
	>
		<div
			className={cn(
				'border-primary absolute z-10 size-3.5 rounded-full border bg-white transition-transform',
				{
					'right-5 -bottom-2': position === 'bottom',
					'-top-2 left-5': position === 'top',
					'scale-125': draggable.transform,
				},
			)}
		/>
	</div>
)

const BookingDuration = ({ duration }: { duration: number }) => (
	<div className='absolute bottom-0 ml-1 flex flex-row items-center gap-x-1 text-xs font-medium'>
		<ClockIcon size={12} />
		<Duration duration={duration} />
	</div>
)

const UnavailableMessage = () => {
	const { t } = useLingui()

	return (
		<p className='absolute right-0 bottom-0 mr-2 text-xs font-bold'>
			{t`Créneau indisponible`}
		</p>
	)
}

export const DraggableTimeslot = ({
	isAvailable,
	isTransforming,
	bounds,
	value,
}: {
	bounds: TimeslotBounds
	isTransforming: boolean
	isAvailable?: boolean
	value: BookingOption
}) => {
	const body = useDraggable({ id: 'draggable-timeslot' })
	const resizeTop = useDraggable({ id: 'draggable-timeslot-resize-top' })
	const resizeBottom = useDraggable({ id: 'draggable-timeslot-resize-bottom' })

	const getStyle = useCallback(() => {
		let top = bounds.startY
		let height = bounds.height

		if (body.transform) {
			top += body.transform.y
		}

		if (resizeTop.transform) {
			top += resizeTop.transform.y
			height -= resizeTop.transform.y
		}

		if (resizeBottom.transform) {
			height = Math.max(
				bounds.height + resizeBottom.transform.y,
				TIME_SLOT_HEIGHT,
			)
		}

		return {
			height,
			top,
			transition: isTransforming ? 'none' : undefined,
		}
	}, [
		bounds,
		body.transform,
		resizeBottom.transform,
		resizeTop.transform,
		isTransforming,
	])

	const bookingDuration = useMemo(() => {
		const start = getTimeAsMinutes(value.start)
		const end = getTimeAsMinutes(value.end)

		return end - start
	}, [value])

	return (
		<div
			ref={body.setNodeRef}
			{...body.listeners}
			{...body.attributes}
			className={cn(
				'border-primary bg-primary bg-primary absolute left-0 flex w-full cursor-move touch-none flex-col justify-between border text-white transition-[background-color,top]',
				{ 'text-primary bg-slate-300/50': !isAvailable },
			)}
			style={getStyle()}
		>
			<ResizeHandle
				draggable={resizeTop}
				position='top'
			/>

			<ResizeHandle
				draggable={resizeBottom}
				position='bottom'
			/>

			<BookingDuration duration={bookingDuration} />

			{!isAvailable && <UnavailableMessage />}
		</div>
	)
}
